import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'wouter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PortfolioTab } from '../components/PortfolioTab';
import { PerformanceTab } from '../components/PerformanceTab';
import { SuiteIntakeTab } from '../components/SuiteIntakeTab';
import { FileNotesTab } from '../components/FileNotesTab';
import { TimelineTab } from '../components/TimelineTab';
import { ComplianceTab } from '../components/ComplianceTab';
import { AdviceTab } from '../components/AdviceTab';
import { QASkeleton } from '../components/QASkeleton';
import { QAEmpty } from '../components/QAEmpty';
import { StatusBadge } from '../components/CompactUI';
import { NextTaskDialog } from '../components/NextTaskDialog';
import { SnapshotDashboard } from '../components/SnapshotDashboard';
import { formatAUD, formatDateAU, calculateAge } from '../../../shared/format';
import { calculateTaxAU2024, calculateFinancialSurplus } from '../../../shared/calculations';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';
import { Phone, Mail, MessageSquare, ExternalLink, Calendar, Clock, Archive } from 'lucide-react';
import type { ClientFullResponse, TransactionRow } from '../../../shared/api';


type TabKey = 'dashboard' | 'timeline' | 'portfolio' | 'performance' | 'transactions' | 'notes' | 'advice' | 'compliance' | 'intake' | 'documents';

export function ClientDetail() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [tab, setTab] = useState<TabKey>('dashboard');
  const { user } = useAuth();
  const qc = useQueryClient();
  const [quickMsg, setQuickMsg] = useState<string | null>(null);
  const [dialogKind, setDialogKind] = useState<'call' | 'schedule' | null>(null);
  const [localTime, setLocalTime] = useState(new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));

  useEffect(() => {
    const timer = setInterval(() => {
      setLocalTime(new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['client', params.id, 'full'],
    queryFn: () => api<{ data: ClientFullResponse }>(`/api/clients/${params.id}/full`),
  });

  if (isLoading) return <QASkeleton variant="detail" />;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load client</div>;

  const c = data.data.client;
  const portfolio = data.data.portfolio;

  const segmentVariants: Record<string, 'diamond' | 'gold' | 'silver' | 'prospect'> = {
    Diamond: 'diamond',
    Platinum: 'gold',
    Gold: 'gold',
    Silver: 'silver',
    Bronze: 'prospect',
  };

  const age = calculateAge(c.dateOfBirth);
  const tax = calculateTaxAU2024(c.annualIncomeCents ?? 0);
  const surplus = calculateFinancialSurplus({
    annualIncomeCents: c.annualIncomeCents ?? 0,
    lifestyleExpensesCents: c.lifestyleExpensesCents ?? 0,
    taxPayableCents: tax
  });

  const canEditClient = can(user?.role, 'client.edit');

  async function archiveClient() {
    if (!window.confirm(`Archive ${c.firstName} ${c.surname}? They'll drop off the active list but can be restored from the dashboard's Archived view.`)) return;
    try {
      await api(`/api/clients/${c.id}/archive`, { method: 'POST' });
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
      setLocation('/');
    } catch {
      setQuickMsg('Could not archive this client.');
      window.setTimeout(() => setQuickMsg(null), 4000);
    }
  }

  return (
    <div className="flex flex-col h-full -mt-6">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-hairline py-3 px-6 lg:px-10 -mx-6 lg:-mx-10 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <StatusBadge label={c.segment ?? 'Unsegmented'} variant={segmentVariants[c.segment ?? ''] ?? 'neutral'} />
                <span className="text-xs font-mono text-ink-mute uppercase tracking-widest">{c.id.split('-')[0]}</span>
              </div>
              <h1 className="text-xl font-bold text-ink flex items-center gap-2">
                {c.surname.toUpperCase()}, {c.firstName}
                {c.informalName && <span className="text-ink-mute font-normal text-lg">({c.informalName})</span>}
              </h1>
            </div>
            <div className="h-10 w-px bg-hairline mx-2" />
            <div className="flex flex-col">
              <span className="text-[10px] uppercase text-ink-mute font-bold tracking-tight flex items-center gap-1">
                <Clock size={10} /> {c.timezone || 'Australia/Sydney'}
              </span>
              <span className="text-sm font-mono font-bold text-ink">{localTime}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
             {/* Alert Badges */}
             <div className="flex gap-1 mr-4">
                {c.soaNeeded && <div className="w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse" title="SoA Outstanding">!</div>}
                {c.reviewDue && new Date(c.reviewDue) < new Date() && <div className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-bold" title="Review Overdue">R</div>}
             </div>

             {/* Quick Actions */}
             <div className="flex gap-1">
                <button
                  type="button"
                  disabled={!canEditClient}
                  onClick={() => setDialogKind('call')}
                  aria-label="Log a call (creates a task in Next-Task)"
                  title="Log a call (creates a task in Next-Task)"
                  className={`p-2 border border-hairline rounded transition-colors ${canEditClient ? 'hover:bg-slate-50 text-ink' : 'text-ink-mute/60 cursor-not-allowed'}`}
                >
                  <Phone size={14} />
                </button>
                <a
                  href={c.email1 ? `mailto:${c.email1}` : undefined}
                  aria-label={c.email1 ? `Email ${c.email1}` : 'No email address on file'}
                  title={c.email1 ? `Email ${c.email1}` : 'No email address on file'}
                  className={`p-2 border border-hairline rounded transition-colors inline-flex items-center justify-center ${c.email1 ? 'hover:bg-slate-50 text-ink' : 'text-ink-mute/60 pointer-events-none'}`}
                >
                  <Mail size={14} />
                </a>
                <button
                  type="button"
                  aria-label="Add file note"
                  title="Add file note"
                  onClick={() => setTab('notes')}
                  className="p-2 border border-hairline rounded hover:bg-slate-50 transition-colors"
                >
                  <MessageSquare size={14} />
                </button>
                <button
                  type="button"
                  disabled={!canEditClient}
                  onClick={() => setDialogKind('schedule')}
                  aria-label="Schedule a follow-up (creates a task in Next-Task)"
                  title="Schedule a follow-up (creates a task in Next-Task)"
                  className={`p-2 border border-hairline rounded transition-colors ${canEditClient ? 'hover:bg-slate-50 text-ink' : 'text-ink-mute/60 cursor-not-allowed'}`}
                >
                  <Calendar size={14} />
                </button>
                <button
                  type="button"
                  aria-label="Open documents"
                  title="Open documents"
                  onClick={() => setTab('documents')}
                  className="p-2 border border-hairline rounded hover:bg-slate-50 transition-colors"
                >
                  <ExternalLink size={14} />
                </button>
                {canEditClient && (
                  <button
                    type="button"
                    onClick={archiveClient}
                    aria-label="Archive this client"
                    title="Archive this client"
                    className="p-2 border border-hairline rounded transition-colors text-ink-mute hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200"
                  >
                    <Archive size={14} />
                  </button>
                )}
             </div>
          </div>
        </div>

        {quickMsg && (
          <div className="mt-1 text-[11px] text-rust font-semibold" role="status" aria-live="polite">{quickMsg}</div>
        )}

        {dialogKind && (
          <NextTaskDialog
            clientId={c.id}
            clientName={[c.firstName, c.surname].filter(Boolean).join(' ').trim() || 'client'}
            kind={dialogKind}
            onClose={() => setDialogKind(null)}
            onCompleted={(msg) => {
              setQuickMsg(msg);
              void qc.invalidateQueries({ queryKey: ['client', c.id, 'full'] });
              window.setTimeout(() => setQuickMsg(null), 4000);
            }}
          />
        )}

        {/* Dense Tabs */}
        <div className="flex gap-4 mt-3 border-t border-hairline pt-2 overflow-x-auto no-scrollbar">
          {([
            ['dashboard', 'Snapshot'],
            ['timeline', 'Timeline'],
            ['portfolio', 'Holdings'],
            ['performance', 'Performance'],
            ['transactions', 'Activity'],
            ['notes', 'File Notes'],
            ['advice', 'Strategy'],
            ['compliance', 'Compliance'],
            ['intake', 'Intake'],
            ['documents', 'Vault'],
          ] as Array<[TabKey, string]>).map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`text-[10px] uppercase font-bold tracking-widest pb-1 border-b-2 transition-colors whitespace-nowrap ${tab === k ? 'border-rust text-ink' : 'border-transparent text-ink-mute hover:text-ink'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="py-6">
        {tab === 'dashboard' && <SnapshotDashboard client={c} portfolio={portfolio} members={data.data.members ?? []} feeMetrics={data.data.feeMetrics} age={age} tax={tax} surplus={surplus} canEdit={can(user?.role, 'client.edit')} />}
        {tab === 'timeline' && <TimelineTab clientId={c.id} onOpenTab={(t) => setTab(t as TabKey)} />}
        {tab === 'portfolio' && <PortfolioTab portfolio={portfolio} />}
        {tab === 'performance' && <PerformanceTab clientId={c.id} portfolio={portfolio} />}
        {tab === 'transactions' && <TransactionsTab clientId={c.id} />}
        {tab === 'notes' && <FileNotesTab clientId={c.id} initial={data.data.recentNotes} />}
        {tab === 'advice' && <AdviceTab clientId={c.id} canCreate={can(user?.role, 'advice.create')} canApprove={can(user?.role, 'advice.approve')} />}
        {tab === 'compliance' && <ComplianceTab client={c} canEdit={can(user?.role, 'client.edit')} />}
        {tab === 'intake' && <SuiteIntakeTab clientId={c.id} />}
        {tab === 'documents' && <DocumentsTab />}
      </div>
    </div>
  );
}

function TransactionsTab({ clientId }: { clientId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['client', clientId, 'transactions'],
    queryFn: () => api<{ data: TransactionRow[] }>(`/api/clients/${clientId}/transactions`),
  });
  if (isLoading) return <div className="mt-6"><QASkeleton variant="table" rows={12} /></div>;
  const rows = data?.data ?? [];
  if (rows.length === 0) return <QAEmpty inline title="No transactions yet." />;
  return (
    <div className="overflow-x-auto border border-hairline rounded shadow-sm">
      <table className="qa-table text-xs">
        <thead className="bg-slate-50">
          <tr>
            <th className="py-2">Trade date</th><th>Type</th><th>Security</th>
            <th className="text-right">Units</th><th className="text-right">Price</th>
            <th className="text-right">Total</th><th className="text-right">Fee</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} className="hover:bg-slate-50 transition-colors">
              <td className="py-1.5 font-mono">{formatDateAU(t.trade_date)}</td>
              <td><span className={`px-1 rounded text-[9px] font-bold uppercase ${t.contract_type === 'buy' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{t.contract_type}</span></td>
              <td><div className="flex flex-col"><span className="font-bold">{t.security_code}</span><span className="text-[10px] text-ink-mute truncate max-w-[150px]">{t.security_name}</span></div></td>
              <td className="text-right font-mono">{Number(t.units).toLocaleString('en-AU')}</td>
              <td className="text-right font-mono">{formatAUD(Number(t.price_cents) / 100)}</td>
              <td className="text-right font-mono font-bold">{formatAUD(Number(t.consideration_cents) / 100)}</td>
              <td className="text-right font-mono text-ink-mute">{formatAUD(Number(t.brokerage_cents) / 100)}</td>
              <td><span className="text-[10px] uppercase text-ink-mute">{t.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocumentsTab() {
  return (
    <div className="bg-white border border-hairline rounded p-12 text-center text-ink-mute shadow-sm">
      <div className="mx-auto w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-4">
        <ExternalLink size={24} className="text-slate-300" />
      </div>
      <p className="text-sm font-bold text-ink mb-1">Secure Vault</p>
      <p className="text-xs">SharePoint / OneDrive integration pending.</p>
    </div>
  );
}
